import { useState, useEffect, useMemo } from "react";
import { usePatientContext } from "Context/Patient";
import { GetMedicalHistory, GetPatientHealthTimeline } from "Services/API/Patients.service";
import { buildChangeLog } from "./healthProfileChanges";
import { getMedicalHistoryFormatter } from "Helpers/Patients";
import {
  EMPTY_VALUE,
  Field,
  None,
  Section,
  ValueList,
} from "Components/Patients/EditViewPatient/Forms/DetailSection/DetailSection";
import "Components/Patients/EditViewPatient/Forms/FormCommonCss/Form.css";
import "Colors.css";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";

// Allergy records predate the current intake form: reaction can be absent,
// empty, or the literal string "null" from older writes. All of those read as
// "not recorded", not as a value worth showing.
const formatReaction = (reaction) => {
  const value = typeof reaction === "string" ? reaction.trim() : reaction;
  if (value === null || value === undefined || value === "" || value === "null") {
    return "NA";
  }
  return value;
};

// A signed note embeds the patient's whole profile at signing time
// (signNote() in backend-core), so each signed visit is an immutable
// point-in-time copy. Its keys differ slightly from the live endpoint's, so
// map them onto the shape getMedicalHistoryFormatter already understands
// rather than writing a second renderer.
const snapshotToFormatterInput = (snapshot) => ({
  bmi: snapshot?.bmi ?? {
    feet: snapshot?.feet,
    inches: snapshot?.inches,
    weightLbs: snapshot?.weightLbs,
  },
  alcohol: snapshot?.alcohol,
  tobacco: snapshot?.tobacco,
  sexualHistory: snapshot?.sexualHistory,
  userAllergies: snapshot?.userAllergies,
  familyHistory: snapshot?.familyHistory,
  // note stores userMedicalConditions (capital C); the live payload uses a
  // lowercase c — normalise here so one renderer serves both.
  userMedicalconditions: snapshot?.userMedicalConditions,
  userImmunization: snapshot?.userImmunization,
  userSurgical: snapshot?.userSurgical,
});

// Inline values that skip <Field>'s own empty check still need the shared
// grey "None" rather than the formatter's "-".
const orNone = (v) =>
  v === null || v === undefined || v === "" || v === "-" ? <None /> : v;

// Signed date is the real capture time; the visit dates are fallbacks for
// older notes that never stored one.
const pointDate = (entry) =>
  entry.signedDate || entry.visitEndedAt || entry.visitStartedAt;

const fmtVisitLabel = (entry) => {
  const when = pointDate(entry);
  const date = when
    ? new Date(when).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })
    : "Unknown date";
  return entry.visitReason ? `${date} — ${entry.visitReason}` : date;
};

// Height/weight only mean something together, and "7'8\"" with a missing
// inches value used to render as `-'-"`. Build the string from what exists.
const fmtHeight = (feet, inches) => {
  const hasFeet = feet !== undefined && feet !== null && feet !== "-" && feet !== "";
  const hasInches =
    inches !== undefined && inches !== null && inches !== "-" && inches !== "";
  if (!hasFeet && !hasInches) return null;
  return `${hasFeet ? feet : 0}'${hasInches ? inches : 0}"`;
};

export function MedicalHistoryFormView() {
  const { getPatientData } = usePatientContext();
  const [medicalHistory, setMedicalHistory] = useState(null);
  // getMedicalHistoryFormatter does not carry medications through, so hold the
  // raw payload for that one section.
  const [liveMedications, setLiveMedications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState("");
  const [timeline, setTimeline] = useState([]);
  const { id } = getPatientData() || {};

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      setApiError("");
      GetMedicalHistory(id)
        .then((res) => {
          setMedicalHistory(getMedicalHistoryFormatter(res));
          setLiveMedications(res?.userMedications || []);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("[GetMedicalHistory ][API_ERROR} -", err);
          setApiError("Failed to load medical history. Please try again.");
          setIsLoading(false);
        });
    }
  }, [id]);

  // Signed notes only: an unsigned note carries no snapshot, so offering it
  // would show an empty profile that looks like data loss.
  useEffect(() => {
    if (!id) return;
    GetPatientHealthTimeline(id)
      .then((res) => setTimeline((res?.data || []).filter((e) => e.signed && e.snapshot)))
      .catch((err) => console.error("[GetPatientHealthTimeline][API_ERROR] -", err));
  }, [id]);

  // Always the LIVE profile — the old "show me one past snapshot" dropdown is
  // replaced by the change log below, which answers the same question without
  // making the reader hold two screens in their head.
  const medications = liveMedications;
  const shown = medicalHistory;

  // Oldest → newest, then the live profile as the final point, so the log ends
  // with "what changed since the last signed note".
  const changeLog = useMemo(() => {
    const points = [...timeline]
      .sort((a, b) => new Date(pointDate(a)) - new Date(pointDate(b)))
      .map((entry) => ({
        key: entry.visitId,
        title: fmtVisitLabel(entry),
        subtitle: "Signed note snapshot",
        profile: getMedicalHistoryFormatter(snapshotToFormatterInput(entry.snapshot)),
        medications: entry.medications || [],
      }));
    if (medicalHistory) {
      points.push({
        key: "current",
        title: "Current profile",
        subtitle: "What the patient can still edit",
        profile: medicalHistory,
        medications: liveMedications,
      });
    }
    return points.length > 1 ? buildChangeLog(points) : [];
  }, [timeline, medicalHistory, liveMedications]);

  return !isLoading ? (
    <>
      <div className="FormWrapper flex flex-col gap-4">
        {apiError && (
          <span className="text-sm text-red-600">{apiError}</span>
        )}
        <div className="flex flex-col gap-4">
          <Section title="Vitals" columns={2}>
            <Field
              label="Height"
              value={fmtHeight(shown?.heightFeet, shown?.heightInches)}
            />
            <Field
              label="Weight"
              value={shown?.weight !== "-" ? `${shown?.weight} lbs` : null}
            />
          </Section>

          <Section title="Medications reported by patient" columns={1}>
            <Field label="Medications">
              <ValueList
                items={medications}
                renderItem={(m) =>
                  `${m?.medicationId?.name || m?.name || EMPTY_VALUE}` +
                  `${m?.dosage ? ` - ${m.dosage}` : ""}` +
                  `${m?.frequency ? ` (${m.frequency})` : ""}`
                }
              />
            </Field>
          </Section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section title="Medical Conditions" columns={1}>
              <Field label="Conditions">
                <ValueList items={shown?.medicalConditions} />
              </Field>
            </Section>

            <Section title="Allergies" columns={1}>
              <Field label="Allergies">
                {shown?.allergies?.length ? (
                  shown.allergies.map((allergy, index) => (
                    <span className="flex flex-col gap-0.5" key={index}>
                      <span>Name: {allergy.name}</span>
                      <span>Type: {allergy.type}</span>
                      {/* Reaction is optional in the intake form, so a missing
                          one rendered as a literal "null"/blank. Also covers ""
                          and the string "null" some older records carry. */}
                      <span>Reaction: {formatReaction(allergy.reaction)}</span>
                    </span>
                  ))
                ) : (
                  <span>{EMPTY_VALUE}</span>
                )}
              </Field>
            </Section>

            <Section title="Lifestyle" columns={1}>
              <Field label="Alcohol Consumption" value={shown?.alcoholConsumption} />
              <Field label="Tobacco Consumption">
                {/* The formatter yields "-" for unanswered fields, which ??
                    does not catch — map it to the shared None explicitly. */}
                <span>Packs a week: {orNone(shown?.tobacoPerWeek)}</span>
                <span>Number of years: {orNone(shown?.tobacoAnnual)}</span>
              </Field>
              <Field label="Sexual History" value={shown?.sexualHistory} />
            </Section>

            <Section title="Surgical History" columns={1}>
              <Field label="Surgeries">
                <ValueList
                  items={shown?.userSurgical}
                  renderItem={(surgery) => surgery?.name || EMPTY_VALUE}
                />
              </Field>
            </Section>

            <Section title="Family History" columns={1}>
              <Field label="Conditions in family">
                <ValueList items={shown?.familyHistory} />
              </Field>
            </Section>

            <Section title="Immunization History" columns={1}>
              <Field label="Immunizations">
                <ValueList
                  items={shown?.userImmunization?.immuniztionName}
                  renderItem={(immunization, index) =>
                    `${index + 1}: ${immunization?.name}` +
                    (immunization?.subName?.length
                      ? ` (${immunization.subName
                          .map((subName) => subName?.name)
                          .join(", ")})`
                      : "")
                  }
                />
              </Field>
            </Section>
          </div>

          {changeLog.length > 0 && (
            <Section title="History — what changed over time" columns={1}>
              <div className="flex flex-col gap-5">
                <span className="text-xs bali_hai">
                  Each entry compares a signed note's snapshot with the one
                  before it. Snapshots are captured when the doctor signs, not
                  when the patient submits intake.
                </span>
                {changeLog.map((entry) => (
                  <ChangeEntry key={entry.key} entry={entry} />
                ))}
              </div>
            </Section>
          )}
        </div>
      </div>
    </>
  ) : (
    isLoading && <Spinner />
  );
}

// One point in the change log: when it was captured and what moved. Reuses the
// page's label/value type ramp — the left rule is the only new affordance, and
// it exists to group a variable number of change lines under one date.
const ChangeEntry = ({ entry }) => (
  <div className="flex flex-col gap-1.5 border-l-2 border-gray-200 pl-4">
    <div className="flex flex-wrap items-baseline gap-x-2">
      <span className="text-sm font-medium sapphire">{entry.title}</span>
      <span className="text-xs bali_hai">{entry.subtitle}</span>
    </div>
    {entry.isBaseline ? (
      <span className="text-sm light_grayish_navy">
        First recorded profile — nothing to compare against.
      </span>
    ) : entry.changes.length === 0 ? (
      <span className="text-sm light_grayish_navy">No changes.</span>
    ) : (
      <ul className="flex flex-col gap-1">
        {entry.changes.map((change) => (
          <li key={change.label} className="text-sm sapphire">
            <span className="light_grayish_navy">{change.label}: </span>
            {change.added || change.removed ? (
              <>
                {change.added?.length > 0 && (
                  <span className="text-green-cyan">
                    added {change.added.join(", ")}
                  </span>
                )}
                {change.added?.length > 0 && change.removed?.length > 0 && ", "}
                {change.removed?.length > 0 && (
                  <span className="text-red-600">
                    removed {change.removed.join(", ")}
                  </span>
                )}
              </>
            ) : (
              <>
                {change.from || EMPTY_VALUE} → {change.to || EMPTY_VALUE}
              </>
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
);
